import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Plus, Minus, Globe, ShieldCheck, ArrowLeft, Loader2 } from "lucide-react";
import logoIcon from "@/assets/logo-warmzapp.webp";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const BASE_PLAN_PRICE = 19;
const BASE_NUMBERS = 2;
const EXTRA_NUMBER_PRICE = 9;

const Checkout = () => {
  const [extraNumbers, setExtraNumbers] = useState(0);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { extraNumbers, globalNetwork: false },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("URL de checkout não retornada");
      }
    } catch (err: any) {
      console.error(err);
      toast.error("Erro ao iniciar checkout. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const totalNumbers = BASE_NUMBERS + extraNumbers;
  const extraNumbersCost = extraNumbers * EXTRA_NUMBER_PRICE;
  const totalMonthly = BASE_PLAN_PRICE + extraNumbersCost;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-warmz-glass-border/20">
        <div className="container mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1 font-display text-xl font-bold text-foreground">
            <img src={logoIcon} alt="WarmZapp" className="h-9 w-auto" />
            Warm<span className="text-primary">Zapp</span>
          </Link>
          <Link to="/signup" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={16} />
            Voltar
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
              Monte o seu plano <span className="gradient-text-fire">ideal</span>
            </h1>
            <p className="text-muted-foreground mt-3 text-lg">
              Comece com o Starter e adicione o que a sua operação precisar.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left – Plan builder */}
            <div className="lg:col-span-3 space-y-6">
              {/* Base plan */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass-card gradient-border p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-xs uppercase tracking-widest text-primary font-semibold">Plano Starter</span>
                    <h3 className="font-display text-xl font-bold text-foreground mt-1">Base Mensal</h3>
                  </div>
                  <div className="text-right">
                    <span className="font-display text-3xl font-bold text-foreground">${BASE_PLAN_PRICE}</span>
                    <span className="text-muted-foreground text-sm">/mês</span>
                  </div>
                </div>
                <ul className="space-y-2">
                  {[
                    `${BASE_NUMBERS} Números Conectados`,
                    "Dashboard Completo",
                    "Simulação Multimédia",
                    "Suporte Prioritário",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-secondary-foreground">
                      <Check className="text-warmz-green-bright shrink-0" size={14} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Extra numbers add-on */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-card p-6"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                    <Plus className="text-primary" size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-lg font-bold text-foreground">Números Extras</h3>
                    <p className="text-sm text-muted-foreground">
                      ${EXTRA_NUMBER_PRICE}/mês por número adicional
                    </p>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Quantidade extra:</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setExtraNumbers(Math.max(0, extraNumbers - 1))}
                      disabled={extraNumbers === 0}
                      className="w-9 h-9 rounded-lg border border-warmz-glass-border flex items-center justify-center text-foreground hover:bg-warmz-card-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="font-display text-xl font-bold text-foreground w-8 text-center">
                      {extraNumbers}
                    </span>
                    <button
                      onClick={() => setExtraNumbers(extraNumbers + 1)}
                      className="w-9 h-9 rounded-lg border border-warmz-glass-border flex items-center justify-center text-foreground hover:bg-warmz-card-hover transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {extraNumbers > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-4 pt-4 border-t border-warmz-glass-border/30 flex justify-between text-sm"
                  >
                    <span className="text-muted-foreground">
                      {extraNumbers} número{extraNumbers > 1 ? "s" : ""} extra{extraNumbers > 1 ? "s" : ""}
                    </span>
                    <span className="font-semibold text-foreground">+${extraNumbersCost}/mês</span>
                  </motion.div>
                )}
              </motion.div>

              {/* Global network add-on – DISABLED */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass-card p-6 opacity-50 cursor-not-allowed"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center shrink-0">
                    <Globe className="text-accent" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-lg font-bold text-foreground">Rede Global</h3>
                      <Badge variant="outline" className="text-[10px] border-warmz-glass-border/50">Em breve</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Blindagem máxima — em breve disponível
                    </p>
                  </div>
                  <div className="w-12 h-7 rounded-full relative bg-warmz-glass-border">
                    <div className="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-foreground" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right – Summary */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-2"
            >
              <div className="glass-card gradient-border p-6 sticky top-8">
                <h3 className="font-display text-lg font-bold text-foreground mb-6">Resumo da Assinatura</h3>

                <div className="space-y-4 text-sm">
                  {/* Base */}
                  <div className="flex justify-between">
                    <span className="text-secondary-foreground">Plano Starter ({BASE_NUMBERS} números)</span>
                    <span className="text-foreground font-medium">${BASE_PLAN_PRICE}</span>
                  </div>

                  {/* Extra numbers */}
                  {extraNumbers > 0 && (
                    <div className="flex justify-between">
                      <span className="text-secondary-foreground">
                        +{extraNumbers} número{extraNumbers > 1 ? "s" : ""} extra{extraNumbers > 1 ? "s" : ""}
                      </span>
                      <span className="text-foreground font-medium">${extraNumbersCost}</span>
                    </div>
                  )}

                  {/* Global network summary removed - Em breve */}

                  <div className="border-t border-warmz-glass-border/30 pt-4">
                    <div className="flex justify-between items-end">
                      <div>
                        <span className="text-muted-foreground text-xs">Total mensal</span>
                        <div className="font-display text-3xl font-bold gradient-text-fire">${totalMonthly}</div>
                      </div>
                      <span className="text-muted-foreground text-xs">/mês</span>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="bg-secondary/50 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-secondary-foreground">
                      <ShieldCheck size={14} className="text-warmz-green-bright" />
                      {totalNumbers} número{totalNumbers > 1 ? "s" : ""} com aquecimento ativo
                    </div>
                    {/* Rede Global em breve */}
                    <div className="flex items-center gap-2 text-xs text-secondary-foreground">
                      <Check size={14} className="text-warmz-green-bright" />
                      Cancele quando quiser
                    </div>
                  </div>
                </div>

                <Button
                  className="w-full h-12 text-base font-bold mt-6 glow-orange-sm"
                  onClick={handleCheckout}
                  disabled={loading}
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processando...</>
                  ) : (
                    <>7 dias grátis, depois ${totalMonthly}/mês</>
                  )}
                </Button>

                <p className="text-center text-xs text-muted-foreground mt-4">
                  Pagamento seguro via Stripe. Sem fidelidade.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Checkout;
